import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth.store';
import type { ApiResponse, AuthPayload } from '@/types/api';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'https://lms-api.vmdigital.in/api/v1';

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().tokens?.accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { tokens, setSession, clear } = useAuthStore.getState();
  if (!tokens?.refreshToken) {
    clear();
    return null;
  }
  try {
    const { data } = await axios.post<ApiResponse<AuthPayload>>(
      `${baseURL}/auth/refresh`,
      { refreshToken: tokens.refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    if (!data?.data) throw new Error('Invalid refresh response');
    setSession(data.data.user, data.data.tokens);
    return data.data.tokens.accessToken;
  } catch (err) {
    clear();
    toast.error('Your session has expired. Please sign in again.');
    return null;
  }
}

http.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= refreshAccessToken().finally(() => {
        refreshing = null;
      });
      const newToken = await refreshing;
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return http(original);
      }
    }

    const message =
      error.response?.data?.message ??
      error.message ??
      'Something went wrong. Please try again.';

    if (status && status >= 500) {
      toast.error(message);
    }

    return Promise.reject(error);
  },
);

export function unwrap<T>(res: { data: ApiResponse<T> }): T {
  return res.data.data;
}
